import {Button} from 'antd';
import {Attachments, Sender} from '@ant-design/x';
import {PlusOutlined, SendOutlined} from '@ant-design/icons';
import {StyledAgentChatSharedAttachmentsPreview} from './StyledAgentChatShared';

/**
 * The antd `Sender` + plus-button/file-input prefix shared by every agent
 * chat entry point (FullWidthAgentChat's footer, QuickAgentAction's card).
 */
export const AgentSenderInput = ({
  senderRef,
  fileInputRef,
  value,
  onChange,
  onSubmit,
  onFileChange,
  onPlusClick,
  placeholder,
  disabled,
  attachmentDrafts = [],
  onRemoveAttachment,
  onAttachmentsChange,
}) => {
  // `Sender`'s own SendButton/Enter handling only look at typed text, so an attachment with no
  // text leaves both permanently disabled (H1MVP-4984) — take over that one case explicitly.
  // Must never fire when there IS text, or the normal send double-submits: the library's own
  // internal handling already owns that case (still guarded by its own text check).
  const canSubmitAttachmentOnly = !disabled && !value.trim() && attachmentDrafts.length > 0;

  const senderActions = (_, {components}) => {
    const {SendButton} = components;
    return (
      <SendButton
        type='text'
        icon={<SendOutlined />}
        disabled={disabled || (!value.trim() && attachmentDrafts.length === 0)}
        onClick={() => { if (canSubmitAttachmentOnly) onSubmit(); }}
      />
    );
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (canSubmitAttachmentOnly) {
      e.preventDefault();
      onSubmit();
    }
  };

  const plusButton = (
    <Button
      disabled={disabled}
      type='text'
      icon={<PlusOutlined />}
      shape='circle'
      onClick={onPlusClick}
    />
  );

  const hiddenFileInput = (
    <input
      className='d-none'
      ref={fileInputRef}
      type='file'
      multiple
      onChange={onFileChange}
      disabled={disabled}
    />
  );

  // Stages the selected attachment(s) above the input instead of sending them — they go out
  // together with whatever the user types once they submit. The Attachments component's own
  // "add more" tile/drop area lets the user attach another file right next to the previews.
  const header = (
    <Sender.Header closable={false} forceRender open={attachmentDrafts.length > 0}>
      {attachmentDrafts.length > 0 && (
        <StyledAgentChatSharedAttachmentsPreview>
          <Attachments
            items={attachmentDrafts}
            onChange={onAttachmentsChange}
            onRemove={onRemoveAttachment}
            beforeUpload={() => false}
            overflow='scrollX'
            disabled={disabled}
          />
        </StyledAgentChatSharedAttachmentsPreview>
      )}
    </Sender.Header>
  );

  return (
    <Sender
      ref={senderRef}
      disabled={disabled}
      value={value}
      onChange={onChange}
      autoSize={{minRows: 1, maxRows: 6}}
      placeholder={placeholder}
      onSubmit={onSubmit}
      onKeyDown={handleKeyDown}
      header={header}
      prefix={<>{plusButton}{hiddenFileInput}</>}
      actions={senderActions}
    />
  );
};
